import dagre from '@dagrejs/dagre';
import type { Edge } from '@xyflow/react';

import type { CustomNode } from '@/sections/attacks/attackPaths/types';

/**
 * Estimate rendered node dimensions by type.
 * Dimensions should approximate the visual size of each node
 * to produce a compact, horizontally-flowing layout.
 *
 * LOCKSTEP WARNING: the node components hard-code spacing derived from these
 * dimensions so each node centers on dagre's rank/column centerlines.
 * HEIGHT (flat edges): each handle lands on the rank centerline (dims.height/2)
 * — circles: top padding pt = (height-72)/2; technique: wrapper h = height.
 * WIDTH (straight columns): each component's outer wrapper width must equal the
 * dims.width returned here (asset/vuln 160, attacker 120, technique 200) and keep
 * the circle/card centered (items-center). That way the node's visual center sits
 * on dagre's column center and `pos.x - dims.width/2` (below) centers it exactly.
 * If you change a width or height here, update the matching node component in
 * lockstep (AssetNode/VulnerabilityNode/AttackerNode/TechniqueNode).
 */
export function getNodeDimensions(node: {
  type?: string;
  data?: Record<string, unknown>;
}): {
  width: number;
  height: number;
} {
  switch (node.type) {
    case 'asset':
      // 72px circle + name + hostname label below (~48). Reserve real height so
      // same-rank nodes aren't stacked tight enough for labels to collide.
      return { width: 160, height: 130 };
    case 'technique':
      // Rounded rectangle, max-w-[200px], T-code + name (~2 lines).
      return { width: 200, height: 72 };
    case 'vulnerability':
      // 72px bug circle + label below — same footprint as asset, not 200 wide.
      return { width: 160, height: 130 };
    case 'attacker':
      // 72px circle + label below.
      return { width: 120, height: 130 };
    default:
      return { width: 120, height: 100 };
  }
}

export interface LayoutOptions {
  direction: 'TB' | 'LR' | 'BT' | 'RL';
  nodeSpacing: number;
  rankSpacing: number;
}

export const DEFAULT_OPTIONS: LayoutOptions = {
  direction: 'LR', // Left-to-Right: attack chains flow horizontally
  // Vertical gap between nodes sharing a rank. Wide enough that fan-out
  // siblings (e.g. the SQLi technique nodes) don't crowd their labels.
  nodeSpacing: 90,
  // Horizontal gap between ranks. xyflow ignores the `curvature` prop for
  // forward (Right→Left) edges and instead sets each bezier control offset to
  // 0.5 × the horizontal handle gap — so a wider rank gap, NOT curvature, is
  // what gives auto-arranged edges their smooth sweep. The handle gap only
  // tracks this value approximately (dagre halves ranksep internally, and a
  // node's reserved width differs from its rendered DOM width), but widening
  // it reliably widens the curve. Keep it generous.
  rankSpacing: 240,
};

/**
 * Pure dagre layout computation for attack-path nodes.
 *
 * Builds a dagre graph from the given nodes/edges, runs the layout, and returns
 * NEW node objects with updated top-left `position` (dagre reports node centers,
 * so each is shifted by half its estimated dimensions). This function does not
 * touch ReactFlow state — no useReactFlow, setNodes, or fitView — so it can be
 * unit-tested with real dagre and no provider plumbing.
 *
 * Empty input is a no-op: the original `nodes` array is returned unchanged.
 */
export function computeLayout(
  nodes: CustomNode[],
  edges: Edge[],
  options: Partial<LayoutOptions> = {}
): CustomNode[] {
  // No-op for empty input — return the input as-is (simplest correct behavior).
  if (nodes.length === 0) return nodes;

  const opts = { ...DEFAULT_OPTIONS, ...options };

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: opts.direction,
    nodesep: opts.nodeSpacing,
    ranksep: opts.rankSpacing,
  });

  nodes.forEach(node => {
    const dims = getNodeDimensions(node);
    dagreGraph.setNode(node.id, { width: dims.width, height: dims.height });
  });

  (edges ?? []).forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  return nodes.map(node => {
    const pos = dagreGraph.node(node.id);
    const dims = getNodeDimensions(node);
    return {
      ...node,
      position: {
        x: pos.x - dims.width / 2,
        y: pos.y - dims.height / 2,
      },
    };
  });
}
